import React, { useState } from "react";
import PublicNavbar from "../../components/public/PublicNavbar";
import InquiryForm from "../../components/public/InquiryForm";
import { FaPlay, FaExpand, FaTimes, FaWhatsapp, FaFileInvoiceDollar } from "react-icons/fa";
import { buildWhatsAppUrl } from "../../utils/whatsapp";

const galleryMedia = [
    {
        type: "video",
        src: "/assets/lumigo/video-demo-1.mp4",
        poster: "/assets/lumigo/projector-1.jpg",
        title: "Outdoor Architectural Gobo Rotation Demo",
        category: "Videos",
        description: "Live demonstration of multi-logo rotation on building facade."
    },
    {
        type: "video",
        src: "/assets/lumigo/video-demo-2.mp4",
        poster: "/assets/lumigo/projector-3.jpg",
        title: "High-Lumen Crisp Focus Calibration",
        category: "Videos",
        description: "Optical sharpness and uniform contrast test."
    },
    {
        type: "video",
        src: "/assets/lumigo/video-demo-3.mp4",
        poster: "/assets/lumigo/projector-5.jpg",
        title: "Dynamic Pattern & Rangoli Projection",
        category: "Videos",
        description: "Smooth 360-degree rotation effect demo."
    },
    // Photos
    { type: "image", src: "/assets/lumigo/projector-1.jpg", title: "DXAX-150T Outdoor Projector Chassis", category: "Hardware" },
    { type: "image", src: "/assets/lumigo/projector-2.jpg", title: "OSRAM Optical Engine Assembly", category: "Hardware" },
    { type: "image", src: "/assets/lumigo/projector-3.jpg", title: "Weatherproof IP65 Gasket Sealing", category: "Hardware" },
    { type: "image", src: "/assets/lumigo/projector-4.jpg", title: "Industrial Safety Floor Stop Sign", category: "Applications" },
    { type: "image", src: "/assets/lumigo/projector-5.jpg", title: "Multi-Logo Glass Gobo Carousel", category: "Hardware" },
    { type: "image", src: "/assets/lumigo/projector-6.jpg", title: "Commercial Hotel Entrance Signage", category: "Applications" },
    { type: "image", src: "/assets/lumigo/projector-7.jpg", title: "Retail Pavement Logo Illumination", category: "Applications" },
    { type: "image", src: "/assets/lumigo/projector-8.jpg", title: "Warehouse Walkway Virtual Striping", category: "Applications" },
    { type: "image", src: "/assets/lumigo/projector-9.jpg", title: "Outdoor Heavy-Duty Mount Bracket", category: "Hardware" },
    { type: "image", src: "/assets/lumigo/projector-10.jpg", title: "Facade Tower Long-Throw Projection", category: "Applications" },
    { type: "image", src: "/assets/lumigo/projector-11.jpg", title: "Indoor Compact 50W Store Unit", category: "Hardware" },
    { type: "image", src: "/assets/lumigo/projector-12.jpg", title: "Event Gala Custom Monogram Gobo", category: "Applications" },
    { type: "image", src: "/assets/lumigo/projector-13.jpg", title: "Precision Focus Ring Mechanism", category: "Hardware" },
    { type: "image", src: "/assets/lumigo/projector-14.jpg", title: "Dynamic Rangoli Cultural Projection", category: "Applications" },
    { type: "image", src: "/assets/lumigo/projector-15.jpg", title: "High-Temperature Dichroic Gobo Disc", category: "Hardware" },
    { type: "image", src: "/assets/lumigo/projector-16.jpg", title: "Factory Floor Hazard Zone Boundary", category: "Applications" },
    { type: "image", src: "/assets/lumigo/projector-17.jpg", title: "300W High-Intensity Optical Head", category: "Hardware" },
    { type: "image", src: "/assets/lumigo/projector-18.jpg", title: "Architectural Exterior Wash & Gobo", category: "Applications" }
];

const Gallery = () => {
    const [selectedCategory, setSelectedCategory] = useState("All");
    const [activeMedia, setActiveMedia] = useState(null);
    const [showInquiry, setShowInquiry] = useState(false);

    const filteredMedia = selectedCategory === "All"
        ? galleryMedia
        : galleryMedia.filter(m => m.category === selectedCategory);

    return (
        <div className="min-h-screen bg-[#FFFFFF] text-[#263746] font-sans">
            <PublicNavbar />

            {/* Header Banner */}
            <div className="bg-[#F7F5EF] border-b border-[#E5E7EB] py-14 px-6 text-center">
                <div className="max-w-4xl mx-auto">
                    <span className="text-[#E3B629] text-xs font-black tracking-[0.25em] uppercase block mb-2">
                        Visual Portfolio
                    </span>
                    <h1 className="text-3xl sm:text-5xl font-extrabold text-[#263746] leading-tight">
                        Installation & Equipment Gallery
                    </h1>
                    <p className="text-[#687482] text-sm sm:text-base mt-4 max-w-2xl mx-auto leading-relaxed">
                        Explore high-power gobo projectors, real projection demonstration videos, industrial safety installations, and architectural facade branding.
                    </p>

                    {/* Filter Tabs */}
                    <div className="flex flex-wrap justify-center gap-2 mt-8">
                        {["All", "Videos", "Hardware", "Applications"].map(cat => (
                            <button
                                key={cat}
                                onClick={() => setSelectedCategory(cat)}
                                className={`px-5 py-2 rounded-full text-xs font-bold uppercase tracking-wider transition-all border ${selectedCategory === cat ? "bg-[#E3B629] text-[#263746] border-[#E3B629] shadow-sm font-extrabold" : "bg-[#FFFFFF] border-[#E5E7EB] text-[#687482] hover:text-[#263746] hover:bg-[#F7F5EF]"}`}
                            >
                                {cat}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Media Grid */}
            <div className="max-w-7xl mx-auto px-6 py-14">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredMedia.map((item, idx) => (
                        <div
                            key={idx}
                            onClick={() => setActiveMedia(item)}
                            className="group relative aspect-[4/3] rounded-2xl overflow-hidden border border-[#E5E7EB] bg-[#F7F5EF] cursor-pointer hover:border-[#E3B629] transition-all duration-300 shadow-sm"
                        >
                            {item.type === "video" ? (
                                <>
                                    <img
                                        src={item.poster}
                                        alt={item.title}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 opacity-90"
                                    />
                                    <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                                        <div className="w-14 h-14 rounded-full bg-[#E3B629] text-[#263746] flex items-center justify-center text-xl shadow-lg group-hover:scale-110 transition-transform">
                                            <FaPlay className="ml-1" />
                                        </div>
                                    </div>
                                    <span className="absolute top-3 right-3 bg-red-600 text-white text-[10px] font-bold px-2 py-0.5 rounded uppercase">
                                        Demo Video
                                    </span>
                                </>
                            ) : (
                                <>
                                    <img
                                        src={item.src}
                                        alt={item.title}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                        loading="lazy"
                                        onError={(e) => { e.target.onerror = null; e.target.src = "/assets/lumigo/projector-1.jpg"; }}
                                    />
                                    <div className="absolute inset-0 bg-black/10 group-hover:bg-black/30 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                                        <div className="w-10 h-10 rounded-full bg-white/80 backdrop-blur-md text-[#263746] flex items-center justify-center text-sm shadow">
                                            <FaExpand />
                                        </div>
                                    </div>
                                </>
                            )}

                            {/* Caption Overlay */}
                            <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black via-black/75 to-transparent">
                                <span className="text-[10px] text-[#E3B629] uppercase font-bold tracking-wider block">
                                    {item.category}
                                </span>
                                <h3 className="text-sm font-bold text-white truncate">
                                    {item.title}
                                </h3>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Media Lightbox / Modal */}
            {activeMedia && (
                <div
                    className="fixed inset-0 z-50 bg-black/75 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8"
                    onClick={() => setActiveMedia(null)}
                >
                    <div
                        className="relative max-w-4xl w-full bg-[#FFFFFF] border border-[#E5E7EB] rounded-2xl overflow-hidden shadow-2xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            onClick={() => setActiveMedia(null)}
                            className="absolute top-4 right-4 z-20 w-8 h-8 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black transition"
                        >
                            <FaTimes />
                        </button>

                        <div className="aspect-video bg-black flex items-center justify-center overflow-hidden">
                            {activeMedia.type === "video" ? (
                                <video
                                    controls
                                    autoPlay
                                    poster={activeMedia.poster}
                                    className="w-full h-full object-contain"
                                >
                                    <source src={activeMedia.src} type="video/mp4" />
                                    Your browser does not support the video tag.
                                </video>
                            ) : (
                                <img
                                    src={activeMedia.src}
                                    alt={activeMedia.title}
                                    className="max-w-full max-h-full object-contain p-4"
                                />
                            )}
                        </div>

                        <div className="p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-t border-[#E5E7EB] bg-[#FFFFFF]">
                            <div>
                                <span className="text-[10px] text-[#E3B629] uppercase font-black tracking-widest block">
                                    {activeMedia.category}
                                </span>
                                <h3 className="text-lg font-bold text-[#263746]">{activeMedia.title}</h3>
                                {activeMedia.description && (
                                    <p className="text-xs text-[#687482] mt-1">{activeMedia.description}</p>
                                )}
                            </div>

                            <div className="flex gap-3">
                                <button
                                    onClick={() => { setActiveMedia(null); setShowInquiry(true); }}
                                    className="bg-[#E3B629] hover:bg-[#cca021] text-[#263746] font-extrabold px-4 py-2 rounded-lg text-xs uppercase tracking-wider flex items-center gap-1.5 transition shadow-sm"
                                >
                                    <FaFileInvoiceDollar /> Request Quote
                                </button>
                                <a
                                    href={buildWhatsAppUrl(`Hello LUMIGO,\n\nI am viewing your gallery item "${activeMedia.title}". Please provide more technical specifications and pricing.`)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="bg-[#168B53] hover:bg-[#127244] text-white font-bold px-4 py-2 rounded-lg text-xs flex items-center gap-1.5 transition shadow-sm"
                                >
                                    <FaWhatsapp /> WhatsApp
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showInquiry && <InquiryForm onClose={() => setShowInquiry(false)} />}
        </div>
    );
};

export default Gallery;
